import React, { Component } from 'react';
import withStyles from '@mui/styles/withStyles';
import { Grid, Checkbox } from '@mui/material';
import compose from 'recompose/compose';
import { change, Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import DetailField from 'components/form/field/field';
import { renderDatePicker } from 'components/form/datepicker/datetime-picker';
import { ADD_FINANCIAL_ASSISTANCE_FORM } from 'constants/index';
import {
  addFinancialAssistanceTask,
  fetchFinancialAssistance,
  addIncome,
  fetchIncomes,
} from 'actions/action-financials';
import {
  validateDate,
  validateDollarValue,
  required,
  validateAlphaNumeric,
  length6,
  maxLength20,
  validateLessThanFiveDigitsValue,
} from 'components/form/validation/validation';
import { stripFormattedDollarValue } from 'services/utils/formatting';

import { convertToArborDate } from 'models/time/arbor-date';
import ConfirmationPanel from 'components/form/confirmation/confirmation-panel';
import {
  renderDropdown,
  renderTextField,
  renderMoneyTextField,
  renderPhoneTextField,
} from 'components/form/field/redux-field';
import { styles } from '../../task-detail-styles';
import { FaIncomeInformationForm } from '../fa-income-information-form';
import FormHeader from '../../../../components/form/header/header';
import { FaOcrInput } from '../../../../components/fa-ocr-input/fa-ocr-input';

const form = `${ADD_FINANCIAL_ASSISTANCE_FORM}_EXTERNAL_FOUNDATION`;
class AddExternalFoundation extends Component {
  constructor(props) {
    super(props);
    this.state = {
      status: null,
      therapyId: null,
      noGroupIdCheckbox: false,
      noPCNCheckbox: false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleGroupIdCheckbox = this.handleGroupIdCheckbox.bind(this);
    this.handleNoPCNCheckbox = this.handleNoPCNCheckbox.bind(this);
  }

  // Handles the submit functionality.
  // Right now it sets some fields to null for simplicity's sake
  handleSubmit(values) {
    const {
      addFinancialAssistanceTask, // eslint-disable-line
      reset,
      therapy,
      cancel,
      task,
      patientId,
      selectedPatientId,
      categoryId,
      addIncome, // eslint-disable-line
      fetchIncomes, // eslint-disable-line
    } = this.props;
    const { noGroupIdCheckbox, noPCNCheckbox } = this.state;
    const payload = {
      ...values,
      patient_id: patientId || selectedPatientId,
      therapy_id: therapy ? therapy.id : values.therapy_id,
      task_fa_id: task ? task.id : null,
      status: 1,
      financial_assistance_type: Number(categoryId),
      group_id: noGroupIdCheckbox ? null : values.group_id,
      foundation_name: values.foundation_name,
      award_amount: stripFormattedDollarValue(values.award_amount),
      expiration_date: values.expiration_date
        ? convertToArborDate(values.expiration_date).getUtcDatetime()
        : null,
      spend_down: values.spend_down ? stripFormattedDollarValue(values.spend_down) : null,
      remaining_balance: values.remaining_balance
        ? stripFormattedDollarValue(values.remaining_balance)
        : null,
      foundation_program: values.foundation_program,
      phone_number: values.phone_number,
      bin: values.bin,
      pcn: noPCNCheckbox ? null : values.pcn,
      member_id: values.member_id,
      copay: values.copay ? stripFormattedDollarValue(values.copay) : null,
    };

    addFinancialAssistanceTask(payload);

    if (values.create_income) {
      const incomePayload = {
        patient_id: patientId || selectedPatientId || values.patient_id,
        taxable_income: values.total_income ? stripFormattedDollarValue(values.total_income) : 0,
        family_size: values.family_size,
        income_year: values.income_year,
      };
      addIncome(incomePayload).then(() => {
        fetchIncomes(patientId || selectedPatientId);
      });
    }

    cancel();
    reset();
  }

  handleGroupIdCheckbox() {
    this.setState(prevState => ({
      noGroupIdCheckbox: !prevState.noGroupIdCheckbox,
    }));
  }

  handleNoPCNCheckbox() {
    this.setState(prevState => ({
      noPCNCheckbox: !prevState.noPCNCheckbox,
    }));
  }

  render() {
    const {
      handleSubmit,
      pristine,
      submitting,
      classes,
      cancel,
      addingFromFinancials,
      isPharmacyBenefit,
      therapies,
      task,
      dispatch,
    } = this.props;

    const { status, therapyId, noGroupIdCheckbox, noPCNCheckbox } = this.state;
    const idSuffix = task && task.therapy_id && task.id ? `_${task.therapy_id}_${task.id}` : '';
    return (
      <Grid container justifyContent="center" spacing={7}>
        <Grid item xs={12}>
          <form className={classes.addFormContainer} autoComplete="off">
            <Grid container spacing={2}>
              {isPharmacyBenefit && (
                <FaOcrInput
                  handleReset={() => {
                    this.setState({
                      usingOcrData: false,
                    });
                    dispatch(change(form, 'bin', ''));
                    dispatch(change(form, 'group_id', ''));
                    dispatch(change(form, 'pcn', ''));
                    dispatch(change(form, 'member_id', ''));
                  }}
                  handleData={ocrData => {
                    this.setState({
                      usingOcrData: true,
                    });
                    dispatch(change(form, 'bin', ocrData.data.bin));
                    dispatch(change(form, 'group_id', ocrData.data.group));
                    dispatch(change(form, 'pcn', ocrData.data.pcn));
                    dispatch(change(form, 'member_id', ocrData.data.memberId));
                  }}
                />
              )}
              <FaIncomeInformationForm
                form={form}
                idPrefix="add_FA_foundation_"
                idSuffix={idSuffix}
              />
              <Grid item xs={12}>
                <FormHeader header="External Foundation" />
              </Grid>
              {addingFromFinancials && (
                <Grid item xs={3} className={classes.fieldContainer}>
                  <Field
                    name="therapy_id"
                    label="Therapy *"
                    validate={[required]}
                    component={renderDropdown}
                    fields={Object.values(therapies).map(t => ({
                      label: t.drug_name,
                      value: t.id,
                    }))}
                    state={therapyId}
                    id={`add_FA_foundation_therapy_id${idSuffix}`}
                  />
                </Grid>
              )}
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="foundation_name"
                  label="Foundation Name *"
                  component={renderTextField}
                  validate={[required]}
                  id={`add_FA_foundation_foundation_name${idSuffix}`}
                />
              </Grid>
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="award_amount"
                  label="Award Amount *"
                  component={renderMoneyTextField}
                  validate={[required, validateDollarValue, validateLessThanFiveDigitsValue]}
                  id={`add_FA_foundation_award_name${idSuffix}`}
                />
              </Grid>
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="expiration_date"
                  label="Expiration Date *"
                  component={renderDatePicker}
                  validate={[validateDate, required]}
                  id={`add_FA_foundation_expiration_date${idSuffix}`}
                />
              </Grid>
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="spend_down"
                  label="Spend Down Amount"
                  component={renderMoneyTextField}
                  validate={[validateDollarValue]}
                  id={`add_FA_foundation_spend_down${idSuffix}`}
                />
              </Grid>
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="remaining_balance"
                  label="Remaining Balance"
                  component={renderMoneyTextField}
                  validate={[validateDollarValue]}
                  id={`add_FA_foundation_remaining_balance${idSuffix}`}
                />
              </Grid>
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="phone_number"
                  label="Phone Number *"
                  component={renderPhoneTextField}
                  validate={[required]}
                  id={`add_FA_foundation_phone_number${idSuffix}`}
                />
              </Grid>
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="foundation_program"
                  label="Name of Foundation Program *"
                  component={renderTextField}
                  validate={[required]}
                  id={`add_FA_foundation_foundation_program${idSuffix}`}
                />
              </Grid>
              {isPharmacyBenefit && (
                <Grid item xs={3} className={classes.fieldContainer}>
                  <Field
                    name="bin"
                    label={!this.state.usingOcrData ? 'BIN *' : 'BIN * [OCR]'}
                    component={renderTextField}
                    validate={[required, validateAlphaNumeric, length6]}
                    id={`add_FA_foundation_bin${idSuffix}`}
                  />
                </Grid>
              )}
              {isPharmacyBenefit && (
                <Grid item xs={6}>
                  <Grid container>
                    <Grid item xs={6} className={classes.fieldContainer}>
                      <Field
                        name="pcn"
                        label={
                          !noPCNCheckbox
                            ? `PCN * ${!this.state.usingOcrData ? '' : '[OCR]'}`
                            : `PCN ${!this.state.usingOcrData ? '' : '[OCR]'}`
                        }
                        disabled={noPCNCheckbox}
                        component={renderTextField}
                        validate={!noPCNCheckbox ? [required, maxLength20] : null}
                        id={`add_FA_foundation_pcn${idSuffix}`}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Grid container alignItems="center" justifyContent="flex-start">
                        <Checkbox
                          checked={noPCNCheckbox}
                          color="primary"
                          onChange={this.handleNoPCNCheckbox}
                          value="checkedPCN"
                          label="Secondary"
                          id={`add_FA_foundation_no_pcn${idSuffix}`}
                        />
                        <DetailField defaultValue="No PCN" />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {isPharmacyBenefit && (
                <Grid item xs={3} className={classes.fieldContainer}>
                  <Field
                    name="member_id"
                    label={`Member ID * ${!this.state.usingOcrData ? '' : '[OCR]'}`}
                    component={renderTextField}
                    validate={[required]}
                    id={`add_FA_foundation_member_id${idSuffix}`}
                  />
                </Grid>
              )}
              {!isPharmacyBenefit && (
                <Grid item xs={3} className={classes.fieldContainer}>
                  <Field
                    name="member_id"
                    component={renderTextField}
                    label="Member ID"
                    id={`add_FA_foundation_no_benefit${idSuffix}`}
                  />
                </Grid>
              )}
              {isPharmacyBenefit && (
                <Grid item xs={6}>
                  <Grid container>
                    <Grid item xs={6} className={classes.fieldContainer}>
                      <Field
                        name="group_id"
                        label={
                          !noGroupIdCheckbox
                            ? `Group ID * ${!this.state.usingOcrData ? '' : '[OCR]'}`
                            : `Group ID ${!this.state.usingOcrData ? '' : '[OCR]'}`
                        }
                        validate={!noGroupIdCheckbox ? [required] : null}
                        component={renderTextField}
                        disabled={noGroupIdCheckbox}
                        id={`add_FA_foundation_group_id${idSuffix}`}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Grid container alignItems="center" justifyContent="flex-start">
                        <Checkbox
                          checked={noGroupIdCheckbox}
                          color="primary"
                          onChange={this.handleGroupIdCheckbox}
                          value="checkedA"
                          label="Secondary"
                          id={`add_FA_foundation_no_group_id${idSuffix}`}
                        />
                        <DetailField defaultValue="No Group Id" />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="copay"
                  label="Copay"
                  decimalScale={2}
                  fixedDecimalScale
                  decimalSeparator="."
                  component={renderMoneyTextField}
                  id={`add_FA_foundation_copay${idSuffix}`}
                />
              </Grid>
            </Grid>
            <ConfirmationPanel
              cancelButtonName="add_task_cancel_button"
              submitButtonName="add_task_submit_button"
              handleSubmit={handleSubmit(this.handleSubmit)}
              handleCancel={cancel}
              disableSubmit={pristine || submitting}
            />
          </form>
        </Grid>
      </Grid>
    );
  }
}

function mapStateToProps(state) {
  const { selectedPatientId, therapies } = state;
  return {
    selectedPatientId,
    therapies: therapies.data,
    initialValues: {
      status: 1,
      foundation_name: null,
      award_amount: null,
      expiration_date: null,
      remaining_balance: null,
      spend_down: null,
      foundation_program: null,
      phone_number: null,
      copay: null,
    },
  };
}

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(mapStateToProps, {
    addFinancialAssistanceTask,
    fetchFinancialAssistance,
    addIncome,
    fetchIncomes,
  }),
)(reduxForm({ form })(AddExternalFoundation));
